/**
 * Daily Status (technician roster snapshot) — Phase 2.3.k.1.
 *
 * Owns the topbar "Daily Status" button + dialog. Polls a snapshot of
 * each field-tech's current shift, ticket queue, AI quality, and SLA
 * stats. The dialog auto-refreshes on a timer while open.
 *
 * Loaded as a module before admin.js. The IIFE registers
 * `window.adminViews.dailyStatus`.
 */

// Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
import { escapeHtml, formatRelativeTime, formatDateTimeShort, formatDateTimeFull } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let dailyStatusBtn, dailyStatusDlg, dailyStatusSummary, dailyStatusTechnicians,
      dailyStatusRefreshBtn, dailyStatusExportBtn, dailyStatusTimestamp,
      dailyStatusCloseBtn;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    dailyStatusBtn = document.getElementById('dailyStatusBtn');
    dailyStatusDlg = document.getElementById('dailyStatusDlg');
    dailyStatusSummary = document.getElementById('dailyStatusSummary');
    dailyStatusTechnicians = document.getElementById('dailyStatusTechnicians');
    dailyStatusRefreshBtn = document.getElementById('dailyStatusRefreshBtn');
    dailyStatusExportBtn = document.getElementById('dailyStatusExportBtn');
    dailyStatusTimestamp = document.getElementById('dailyStatusTimestamp');
    dailyStatusCloseBtn = document.getElementById('dailyStatusClose');
  }

  // ---- State ----------------------------------------------------------
  const dailyStatusState = {
    loading: false,
    data: null,
    timer: null,
    day: null,
    error: null,
    refreshIntervalMs: 60000,
    stale: false,
  };

    function clearDailyStatusTimer() {
      if (dailyStatusState.timer) {
        clearInterval(dailyStatusState.timer);
        dailyStatusState.timer = null;
      }
    }

    function ensureDailyStatusTimer(intervalMs) {
      const resolved = Math.max(15000, Number(intervalMs) || 60000);
      if (dailyStatusState.timer && dailyStatusState.refreshIntervalMs === resolved) {
        return;
      }
      clearDailyStatusTimer();
      dailyStatusState.refreshIntervalMs = resolved;
      dailyStatusState.timer = setInterval(() => {
        loadDailyStatus({ silent: true }).catch(() => {});
      }, resolved);
    }

    function renderDailyStatus() {
      if (!dailyStatusSummary || !dailyStatusTechnicians) return;
      const data = dailyStatusState.data;
      if (!data) {
        dailyStatusSummary.innerHTML = dailyStatusState.loading
          ? '<span class="daily-status-loading">Loading status…</span>'
          : '<span class="daily-status-empty">No status data available.</span>';
        dailyStatusTechnicians.innerHTML = '';
        if (dailyStatusTimestamp) dailyStatusTimestamp.textContent = '';
        return;
      }

      const totals = data.totals || {};
      const assignedTotal = Number(totals.assigned ?? 0);
      const completedTotal = Number(totals.completed ?? 0);
      const remainingTotal = Number(totals.remaining ?? 0);
      const completionRateValue = typeof totals.completion_rate === 'number'
        ? `${Math.round(totals.completion_rate * 100)}%`
        : '—';

      dailyStatusSummary.innerHTML = `
        <div class="metric"><strong>${assignedTotal}</strong><span>assigned</span></div>
        <div class="metric"><strong>${completedTotal}</strong><span>completed</span></div>
        <div class="metric"><strong>${remainingTotal}</strong><span>remaining</span></div>
        <div class="metric"><strong>${completionRateValue}</strong><span>completion rate</span></div>
      `;

      if (dailyStatusTimestamp) {
        if (data.generated_at) {
          dailyStatusTimestamp.textContent = `Updated ${formatRelativeTime(data.generated_at)} (${formatDateTimeShort(data.generated_at)})`;
        } else {
          dailyStatusTimestamp.textContent = '';
        }
      }

      const technicians = Array.isArray(data.technicians) ? data.technicians : [];
      if (!technicians.length) {
        dailyStatusTechnicians.innerHTML = '<div class="daily-status-empty">No technician activity for this day.</div>';
      } else {
        dailyStatusTechnicians.innerHTML = '';
        const createMetaSpan = (text) => {
          const span = document.createElement('span');
          span.textContent = text;
          return span;
        };

        technicians.forEach((tech) => {
          const card = document.createElement('article');
          card.className = 'daily-status-card';
          const header = document.createElement('header');
          const title = document.createElement('div');
          title.className = 'daily-status-title';
          title.textContent = tech.display_name || 'Technician';
          header.appendChild(title);

          const completionRate = tech.summary && typeof tech.summary.completion_rate === 'number'
            ? Math.round(tech.summary.completion_rate * 100)
            : null;
          if (completionRate !== null) {
            const badge = document.createElement('span');
            badge.className = 'badge';
            badge.textContent = `${completionRate}% complete`;
            header.appendChild(badge);
          }

          card.appendChild(header);

          const metaLine = document.createElement('div');
          metaLine.className = 'daily-status-meta';
          metaLine.appendChild(createMetaSpan(`Queue ${Number(tech.queue_total || 0)}`));
          if (typeof tech.ai_average_score === 'number') {
            metaLine.appendChild(createMetaSpan(`Quality ${Math.round(tech.ai_average_score)}`));
          }
          if (typeof tech.throughput_recent === 'number') {
            metaLine.appendChild(createMetaSpan(`Resolved 7d ${tech.throughput_recent}`));
          } else if (typeof tech.throughput_total === 'number') {
            metaLine.appendChild(createMetaSpan(`Resolved 30d ${tech.throughput_total}`));
          }
          if (tech.last_submission_at) {
            metaLine.appendChild(createMetaSpan(`Last submit ${formatRelativeTime(tech.last_submission_at)}`));
          }
          card.appendChild(metaLine);

          const sections = document.createElement('div');
          sections.className = 'daily-status-sections';

          const completedSection = document.createElement('div');
          completedSection.className = 'daily-status-list';
          const completedTitle = document.createElement('h4');
          completedTitle.textContent = 'Completed';
          completedSection.appendChild(completedTitle);

          const completedTickets = Array.isArray(tech.tickets?.completed) ? tech.tickets.completed : [];
          if (!completedTickets.length) {
            const empty = document.createElement('div');
            empty.className = 'daily-status-empty';
            empty.textContent = 'No completed tickets yet.';
            completedSection.appendChild(empty);
          } else {
            completedTickets.forEach((item) => {
              const entry = document.createElement('div');
              entry.className = 'daily-status-item';
              const head = document.createElement('div');
              head.className = 'daily-status-item-head';
              const code = document.createElement('span');
              code.className = 'badge';
              const shortCode = item.short_code || String(item.ticket_id || '').slice(0, 8);
              code.textContent = `#${shortCode}`;
              head.appendChild(code);
              if (item.subject) {
                const subject = document.createElement('span');
                subject.textContent = ` ${item.subject}`;
                head.appendChild(subject);
              }
              if (item.carryover) {
                const carry = document.createElement('span');
                carry.className = 'badge warn';
                carry.textContent = 'Carryover';
                head.appendChild(carry);
              }
              entry.appendChild(head);
              const meta = document.createElement('div');
              meta.className = 'daily-status-item-meta';
              if (item.customer_name) meta.appendChild(createMetaSpan(item.customer_name));
              if (item.assigned_at) meta.appendChild(createMetaSpan(`Assigned ${formatDateTimeShort(item.assigned_at)}`));
              if (item.completed_at) meta.appendChild(createMetaSpan(`Completed ${formatDateTimeShort(item.completed_at)}`));
              entry.appendChild(meta);
              completedSection.appendChild(entry);
            });
          }

          const remainingSection = document.createElement('div');
          remainingSection.className = 'daily-status-list';
          const remainingTitle = document.createElement('h4');
          remainingTitle.textContent = 'Remaining';
          remainingSection.appendChild(remainingTitle);
          const remainingTickets = Array.isArray(tech.tickets?.remaining) ? tech.tickets.remaining : [];
          if (!remainingTickets.length) {
            const empty = document.createElement('div');
            empty.className = 'daily-status-empty';
            empty.textContent = 'No open tickets for this technician.';
            remainingSection.appendChild(empty);
          } else {
            remainingTickets.forEach((item) => {
              const entry = document.createElement('div');
              entry.className = 'daily-status-item';
              const head = document.createElement('div');
              head.className = 'daily-status-item-head';
              const code = document.createElement('span');
              code.className = 'badge';
              const shortCode = item.short_code || String(item.ticket_id || '').slice(0, 8);
              code.textContent = `#${shortCode}`;
              head.appendChild(code);
              if (item.subject) {
                const subject = document.createElement('span');
                subject.textContent = ` ${item.subject}`;
                head.appendChild(subject);
              }
              entry.appendChild(head);
              const meta = document.createElement('div');
              meta.className = 'daily-status-item-meta';
              const statusLabel = item.status ? formatTicketStatus(item.status) : 'Pending';
              meta.appendChild(createMetaSpan(statusLabel));
              if (item.address) meta.appendChild(createMetaSpan(item.address));
              if (item.assigned_at) meta.appendChild(createMetaSpan(`Assigned ${formatDateTimeShort(item.assigned_at)}`));
              entry.appendChild(meta);
              remainingSection.appendChild(entry);
            });
          }

          sections.appendChild(completedSection);
          sections.appendChild(remainingSection);
          card.appendChild(sections);
          dailyStatusTechnicians.appendChild(card);
        });
      }

      if (dailyStatusDlg && typeof data.refresh_interval_seconds === 'number' && dailyStatusDlg.open) {
        ensureDailyStatusTimer(data.refresh_interval_seconds * 1000);
      }
    }

    async function loadDailyStatus(options = {}) {
      if (!dailyStatusDlg || (!dailyStatusDlg.open && !options.force)) {
        return;
      }
      const silent = Boolean(options.silent);
      if (dailyStatusState.loading && silent) return;
      dailyStatusState.loading = true;
      if (!silent && dailyStatusSummary) {
        dailyStatusSummary.innerHTML = '<span class="daily-status-loading">Loading status…</span>';
      }
      if (!silent && dailyStatusTechnicians) {
        dailyStatusTechnicians.innerHTML = '';
      }
      if (dailyStatusRefreshBtn) dailyStatusRefreshBtn.disabled = true;
      if (dailyStatusExportBtn) dailyStatusExportBtn.disabled = true;
      try {
        const response = await adminAPI.fetch(API.adminDailyStatus, { method: 'GET' });
        dailyStatusState.data = response;
        dailyStatusState.day = response && response.day ? response.day : null;
        dailyStatusState.error = null;
        dailyStatusState.stale = false;
        renderDailyStatus();
        if (dailyStatusDlg.open) {
          const refreshSeconds = Number(response?.refresh_interval_seconds);
          const intervalMs = Number.isFinite(refreshSeconds) && refreshSeconds > 0 ? refreshSeconds * 1000 : 60000;
          ensureDailyStatusTimer(intervalMs);
        }
      } catch (err) {
        dailyStatusState.error = err;
        if (!silent) showFlash(err?.message || 'Failed to load daily status', 'error');
        if (dailyStatusSummary) {
          dailyStatusSummary.innerHTML = `<span class="daily-status-empty">${escapeHtml(err?.message || 'Failed to load daily status')}</span>`;
        }
        if (dailyStatusTechnicians) dailyStatusTechnicians.innerHTML = '';
      } finally {
        dailyStatusState.loading = false;
        if (dailyStatusRefreshBtn) dailyStatusRefreshBtn.disabled = false;
        if (dailyStatusExportBtn) dailyStatusExportBtn.disabled = false;
      }
    }

    function openDailyStatusDialog() {
      if (!dailyStatusDlg) { console.warn('daily-status: #dailyStatusDlg missing — topbar button has no effect.'); return; }
      if (!dailyStatusDlg.open) {
        openDialog(dailyStatusDlg);
      }
      dailyStatusState.error = null;
      if (dailyStatusSummary) {
        dailyStatusSummary.innerHTML = '<span class="daily-status-loading">Loading status…</span>';
      }
      if (dailyStatusTechnicians) {
        dailyStatusTechnicians.innerHTML = '';
      }
      loadDailyStatus({ force: true }).catch(() => {});
      ensureDailyStatusTimer(dailyStatusState.refreshIntervalMs || 60000);
    }

    function closeDailyStatusDialog() {
      if (!dailyStatusDlg) return;
      clearDailyStatusTimer();
      if (typeof dailyStatusDlg.close === 'function') {
        dailyStatusDlg.close();
      } else {
        dailyStatusDlg.open = false;
      }
    }

    async function exportDailyStatusCsv() {
      if (!dailyStatusExportBtn) return;
      const originalText = dailyStatusExportBtn.textContent;
      dailyStatusExportBtn.disabled = true;
      dailyStatusExportBtn.textContent = 'Downloading…';
      try {
        const endpoint = API.adminDailyStatusExport(dailyStatusState.day);
        const response = await fetch(endpoint, { method: 'GET', credentials: 'include' });
        if (!response.ok) {
          throw new Error('Failed to download CSV');
        }
        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
        const fallbackName = dailyStatusState.day || 'today';
        const filename = match && match[1] ? match[1] : `daily-status-${fallbackName}.csv`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        requestAnimationFrame(() => {
          URL.revokeObjectURL(url);
          link.remove();
        });
      } catch (err) {
        showFlash(err?.message || 'Failed to download CSV', 'error');
      } finally {
        dailyStatusExportBtn.disabled = false;
        dailyStatusExportBtn.textContent = originalText;
      }
    }

  function attachListeners() {
    resolveDom();
    if (dailyStatusBtn) {
      dailyStatusBtn.addEventListener('click', () => {
        openDailyStatusDialog();
      });
    }
    if (dailyStatusCloseBtn) {
      dailyStatusCloseBtn.addEventListener('click', () => {
        closeDailyStatusDialog();
      });
    }
    if (dailyStatusDlg) {
      dailyStatusDlg.addEventListener('close', () => {
        clearDailyStatusTimer();
      });
      dailyStatusDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDailyStatusDialog();
      });
    }
    if (dailyStatusRefreshBtn) {
      dailyStatusRefreshBtn.addEventListener('click', () => {
        loadDailyStatus().catch(() => {});
      });
    }
    if (dailyStatusExportBtn) {
      dailyStatusExportBtn.addEventListener('click', () => {
        exportDailyStatusCsv().catch(() => {});
      });
    }
  }

  function markStale() { dailyStatusState.stale = true; }

  function isOpen() {
    resolveDom();
    return Boolean(dailyStatusDlg && dailyStatusDlg.open);
  }

export default {
  attachListeners,
  state: dailyStatusState,
  load: loadDailyStatus,
  render: renderDailyStatus,
  open: openDailyStatusDialog,
  close: closeDailyStatusDialog,
  markStale,
  isOpen,
};
